import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

/**
 * The canonical Page-card status pill. One mono uppercase pill per Page card,
 * everywhere in the "everything is a Page" surfaces (Front, Desk, Feed, Work,
 * Library, Reader). The tone carries the one rule that governs the whole UI:
 *
 *   - Blue (`needsYou`, `yourGate`) = human attention. There is at most ONE
 *     attention-primary signal in any given view. Reserve it for the real
 *     decision. (Amber is reserved for honesty notices, never pills.)
 *   - Quiet green (`executing`) = agent activity in flight; informational,
 *     never a CTA.
 *   - Tertiary/quiet (`done`, `read`, `neutral`, `brief`) = settled or context.
 *
 * Colours derive from the additive `--pill-*` tokens (tokens.css + the
 * `.workspace-shell` mirror). No hardcoded hex here.
 *
 * @example
 * <StatusPill tone="needsYou" />                       // NEEDS YOU (breathing dot opt-in)
 * <StatusPill tone="needsYou" pulse>Needs you</StatusPill>
 * <StatusPill tone="executing">Executing</StatusPill>  // teal, agent activity
 * <StatusPill tone="yourGate">Your gate</StatusPill>   // amber gate
 * <StatusPill tone="done">Done</StatusPill>            // quiet, settled
 * <StatusPill tone="neutral">Published</StatusPill>
 */
const statusPillVariants = cva(
  "inline-flex items-center gap-1 rounded-full font-mono uppercase tracking-[.08em] text-[9px] px-[11px] py-1 whitespace-nowrap",
  {
    variants: {
      tone: {
        // Blue — human attention. The primary signal; at most one per view.
        needsYou:
          "bg-[var(--pill-needs-you-bg)] text-[var(--pill-needs-you-fg)]",
        // Blue gate — outlined, the human's explicit go/no-go.
        yourGate:
          "border border-[var(--pill-gate-border)] text-[var(--pill-gate-fg)]",
        // Quiet green — agent activity in flight. Informational, never a CTA.
        executing:
          "border border-[var(--pill-executing-border)] text-[var(--pill-executing-fg)]",
        // Quiet — settled.
        done: "text-[var(--pill-done-fg)]",
        // Quiet context tones.
        neutral: "bg-bg-surface text-text-secondary",
        brief: "border border-border text-text-secondary",
        read: "text-text-tertiary",
      },
    },
    defaultVariants: { tone: "neutral" },
  }
);

type StatusPillProps = Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> &
  VariantProps<typeof statusPillVariants> & {
    /**
     * Render a leading breathing amber dot (the `needpulse` keyframe). Only
     * meaningful for the amber attention tones; ignored otherwise.
     */
    pulse?: boolean;
  };

/**
 * StatusPill — see module doc for the one-amber-primary rule.
 *
 * @example
 * <StatusPill tone="needsYou" pulse>Needs you</StatusPill>
 */
export function StatusPill({
  className,
  tone,
  pulse,
  children,
  ...props
}: StatusPillProps) {
  const showDot = Boolean(pulse) && (tone === "needsYou" || tone === "yourGate");
  return (
    <span className={cn(statusPillVariants({ tone }), className)} {...props}>
      {showDot ? (
        <span
          aria-hidden="true"
          className="deccard-needdot"
          data-testid="status-pill-dot"
        />
      ) : null}
      {children}
    </span>
  );
}

export { statusPillVariants };
export type { StatusPillProps };
